import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import { InlineEditorLogic, isEditMode, useInlineEditor } from '@tableau/vizportalclient-common-components';

import { ACTION_DIALOG_ID } from '~src/app/content/contentDetailDialog/ContentDetailsDialog';
import {
  ContentDetailSection,
  DetailEditButton,
  DetailSaveCancelButtons
} from '~src/app/content/contentDetailDialog/ContentDetailSection';
import { selectSessionSite } from '~src/app/selectors/SessionInfoSelectors';
import { selectSpaceInfoItem } from '~src/app/selectors/SpaceInfoSelectors';
import { AppState } from '~src/app/state/AppState';
import { MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { AskDataMode } from '~src/settings/types/AskDataMode';
import { AskDataIndexing, IDataSourceInfo } from '~src/ts/models/IDataSourceInfo';

import { AskDataEditMode } from './AskDataEditMode';
import { AskDataReadingMode } from './AskDataReadingMode';
import { AskDataSettingsEditorState } from './AskDataSettingsEditorState';


// ID for accessibilty purposes
const sectionHeaderId = ACTION_DIALOG_ID + '-AskData';

interface AskDataSettingsSectionOwnProps {
  readonly isAdminOrOwner: boolean;
  readonly setAskDataSettings: (indexing: AskDataIndexing, feedbackSetting: boolean) => Promise<IResult>;
  readonly incrementRouterSpaceReactKey: () => void;
}

interface AskDataSettingsSectionStateProps {
  readonly askDataMode: AskDataMode;
  readonly askDataIndexing: AskDataIndexing;
  readonly feedbackToCurator: boolean;
}

export type AskDataSettingsProps = AskDataSettingsSectionOwnProps & AskDataSettingsSectionStateProps;

export function AskDataSettingsSection(props: AskDataSettingsProps): JSX.Element {
  if (props.askDataMode === AskDataMode.DISABLED_ALWAYS) {
    return <></>;
  }

  const onSave = React.useCallback(async (editorState: AskDataSettingsEditorState) => {
    await props.setAskDataSettings(editorState.askDataIndexing, editorState.feedbackToCurator)
      .then(rejectIfResultContainsError)
      .then(props.incrementRouterSpaceReactKey);
  }, [
    props.askDataIndexing,
    props.feedbackToCurator,
    props.setAskDataSettings,
    props.incrementRouterSpaceReactKey,
  ]);

  const canSave = (editorState: AskDataSettingsEditorState) => {
    return !_.isEqual(editorState, initialState);
  };

  const initialState: AskDataSettingsEditorState = {
    askDataIndexing: props.askDataIndexing,
    feedbackToCurator: props.feedbackToCurator,
  };

  const logic: InlineEditorLogic<AskDataSettingsEditorState> = useInlineEditor<AskDataSettingsEditorState>({
    editorStateWhenEnteringEditMode: initialState,
    canEdit: props.isAdminOrOwner,
    onSave: onSave,
    canSaveFn: canSave,
  });

  if (isEditMode(logic)) {
    return (
      <ContentDetailSection
        header={Messages.nlp()}
        testId={'ask-data'}
        headerId={sectionHeaderId}
        detail={
          <AskDataEditMode
            askDataMode={props.askDataMode}
            askDataIndexing={logic.editorState.askDataIndexing}
            feedbackToCurator={logic.editorState.feedbackToCurator}
            setEditorState={logic.setEditorStateWithinEditMode}
            disableControls={logic.isActionBusy}
          />
        }
        isActionAllowed={true}
        action={<DetailSaveCancelButtons inlineEditorLogic={logic} />}
      />
    );
  }
  else {
    return (
      <ContentDetailSection
        header={Messages.nlp()}
        testId={'ask-data'}
        detail={
          <AskDataReadingMode
            askDataIndexing={props.askDataIndexing}
            feedbackToCurator={props.feedbackToCurator}
          />
        }
        isActionAllowed={logic.canEdit}
        action={
          <DetailEditButton
            buttonLabel={Messages.dataSourceNlpSetting_editNlpSettingLabel()}
            buttonTestId={'edit-ask-data'}
            onClickAction={logic.enterEditMode}
          />
        }
      />
    );
  }
}

const mapStateToProps: MapStateToProps<AskDataSettingsSectionStateProps> = (state: AppState) => ({
  askDataMode: selectSessionSite(state).askDataMode,
  askDataIndexing: (selectSpaceInfoItem(state) as IDataSourceInfo).askDataIndexing,
  feedbackToCurator: (selectSpaceInfoItem(state) as IDataSourceInfo).askDataFeedbackToCuratorEnabled,
});

export const ConnectedAskDataSettingsSection: React.ComponentClass<AskDataSettingsSectionOwnProps> =
  connect(mapStateToProps)(AskDataSettingsSection);

